import { z } from 'zod';
import { OperationIdSchema } from '../../ids';
import { AccessGrantViewSchema, AccessUseSchema, GranteeSchema } from '../../models/access';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** The kinds of good that take grants (CORE-PERMISSION, GA-10). */
export const AccessKindSchema = z.enum(['lot', 'ship']);

/**
 * A good's access as one sees it (CORE-PERMISSION, T3): whoever manages it (its owner, a company's
 * ranks with `grant`, GA-6) sees every grant; anyone else only what it may do itself.
 */
export const AccessViewSchema = z.object({
  kind: AccessKindSchema,
  good: z.string().min(1).max(64),
  /** Whether the asker grants on it. */
  manage: z.boolean(),
  /** Its grants, to a manager; empty to anyone else. */
  grants: z.array(AccessGrantViewSchema),
  /** What the asker may do with it now. */
  mine: z.array(AccessUseSchema),
});
export type AccessView = z.infer<typeof AccessViewSchema>;

/** A good's access (CORE-PERMISSION, T3). */
export const getAccess = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/access/:kind/:good`,
  access: 'session',
  params: z.object({ kind: AccessKindSchema, good: z.string().min(1).max(64) }),
  response: AccessViewSchema,
});

/**
 * Sets what a grantee may do with a good (CORE-PERMISSION, T7): its uses replace any it had, none
 * takes its grant away. Its manager's, from anywhere. Idempotent.
 */
export const setAccess = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/access/:kind/:good`,
  access: 'session',
  params: z.object({ kind: AccessKindSchema, good: z.string().min(1).max(64) }),
  body: z.object({
    operationId: OperationIdSchema,
    grantee: GranteeSchema,
    uses: z.array(AccessUseSchema).max(8),
  }),
  response: AccessViewSchema.extend({ operationId: OperationIdSchema }),
});
